import type { ReactNode } from "react";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";

import Droppable from "@/components/dnd/Droppable";
import CouponSkeleton from "@/components/muiCustom/skeleton/CouponSkeleton";
import RRDDirectMailPlaceholder from "../RRDDirectMailPlaceholder";
import { ASSET_TYPE } from "@/features/campaigns/direct-mail/rrd/utils/rrdConstants";
import { normalizeRRDAssetType } from "@/features/campaigns/direct-mail/rrd/utils/rrdLayoutHelper";
import type {
  RRDAsset,
  RRDLayoutSpec,
  RRDMobileSlotPayload,
  RRDSlot,
  RRDSx,
} from "./layoutTypes";

type RRDImageStyle = React.CSSProperties;

function valuesMatch(
  left: string | number | null | undefined,
  right: string | number | null | undefined
) {
  if (
    left === undefined ||
    left === null ||
    right === undefined ||
    right === null
  )
    return false;
  return String(left) === String(right);
}

function couponMatchesSlot(coupon: RRDAsset, slot: RRDSlot) {
  return (
    valuesMatch(coupon?.slotId, slot.id) ||
    valuesMatch(coupon?.slot_id, slot.id) ||
    valuesMatch(coupon?.order, slot.order) ||
    valuesMatch(coupon?.coupon_number, slot.order) ||
    valuesMatch(coupon?.couponNumber, slot.order)
  );
}

export const StyledDeleteIconButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 5,
  right: 5,
  backgroundColor: theme.palette.error.main,
  color: "var(--palette-error-contrastText)",
  zIndex: 10,
  border: "1px solid var(--neutral-white)",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.error.main,
    transform: "scale(1.1)",
  },
}));

type AssetForSlotParams = {
  slot: RRDSlot;
  heroImages?: RRDAsset[];
  coupons?: RRDAsset[];
};

function getAssetForSlot({
  slot,
  heroImages = [],
  coupons = [],
}: AssetForSlotParams) {
  const slotType = String(normalizeRRDAssetType(slot.assetType));

  if (slotType === ASSET_TYPE.COUPON) {
    return coupons?.find((coupon) => couponMatchesSlot(coupon, slot));
  }

  return heroImages?.find((hero) => {
    if (hero?.slotId === slot.id || hero?.slot_id === slot.id) return true;
    const heroType = normalizeRRDAssetType(hero?.asset_type || hero?.assetType);
    return (
      hero?.side === slot.side &&
      (!heroType || heroType === slotType || slotType === ASSET_TYPE.HERO)
    );
  });
}

function SlotImage({
  asset,
  sx = {},
}: {
  asset?: RRDAsset;
  sx?: RRDImageStyle;
}) {
  if (!asset) return null;
  return (
    <img
      src={asset.compress_url}
      alt={asset.asset_name}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
        ...sx,
      }}
    />
  );
}

type SlotShellProps = {
  slot: RRDSlot;
  selectedAsset?: RRDAsset;
  children: ReactNode;
  placeholder?: string;
  onRemove?: (_slot: RRDSlot) => void;
  readOnly?: boolean;
  sx?: RRDSx;
};

function SlotShell({
  slot,
  selectedAsset,
  children,
  placeholder,
  onRemove,
  readOnly = false,
  sx = {},
}: SlotShellProps) {
  return (
    <Box
      sx={{
        position: "relative",
        "& .trash-icon": { visibility: "hidden", opacity: 0 },
        "&:hover .trash-icon": {
          visibility: selectedAsset && !readOnly ? "visible" : "hidden",
          opacity: 1,
        },
        ...sx,
      }}
    >
      {children}
      {selectedAsset && !readOnly && onRemove && (
        <StyledDeleteIconButton
          className="trash-icon"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onRemove(slot);
          }}
          title={`Remove ${placeholder}`}
        >
          <DeleteIcon sx={{ fontSize: 18 }} />
        </StyledDeleteIconButton>
      )}
    </Box>
  );
}

export function RRDImageSlot({
  slot,
  heroImages,
  coupons,
  mode = "desktop",
  onOpenMobileSlot,
  onRemoveAsset,
  readOnly = false,
  placeholder,
  sx = {},
  imageSx = {},
}: {
  slot: RRDSlot;
  heroImages?: RRDAsset[];
  coupons?: RRDAsset[];
  mode?: string;
  onOpenMobileSlot?: (_slot: RRDMobileSlotPayload) => void;
  onRemoveAsset?: (_slot: RRDSlot) => void;
  readOnly?: boolean;
  placeholder?: string;
  sx?: RRDSx;
  imageSx?: RRDImageStyle;
}) {
  const selectedAsset = getAssetForSlot({ slot, heroImages, coupons });
  const slotType = normalizeRRDAssetType(slot.assetType);
  const isCoupon = slotType === ASSET_TYPE.COUPON;
  const shellSx = sx?.height ? { height: sx.height } : {};

  const content = selectedAsset ? (
    <SlotImage asset={selectedAsset} sx={imageSx} />
  ) : isCoupon && slot.editable === false ? (
    <CouponSkeleton />
  ) : null;

  if (mode === "mobile") {
    return (
      <SlotShell
        slot={slot}
        selectedAsset={selectedAsset}
        placeholder={placeholder}
        onRemove={onRemoveAsset}
        readOnly={readOnly || slot.editable === false}
        sx={shellSx}
      >
        <Box
          sx={(theme) => ({
            background: selectedAsset
              ? "transparent"
              : theme.palette.primary.light,
            width: "100%",
            height: "auto",
            aspectRatio: slot.aspectRatio || "1 / 1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            cursor: slot.editable === false || readOnly ? "default" : "pointer",
            ...sx,
          })}
          onClick={() =>
            slot.editable === false || readOnly
              ? null
              : onOpenMobileSlot?.({
                  side: slot.side,
                  id: slot.id,
                  type: slotType,
                  slot_id: slot.id,
                  slotId: slot.id,
                  coupon_number: slot.order,
                  order: slot.order,
                  assetType: slotType,
                })
          }
        >
          {content || (
            <RRDDirectMailPlaceholder
              placeholder="Click to add"
              subPlaceholder={placeholder}
            />
          )}
        </Box>
      </SlotShell>
    );
  }

  return (
    <SlotShell
      slot={slot}
      selectedAsset={selectedAsset}
      placeholder={placeholder}
      onRemove={onRemoveAsset}
      readOnly={readOnly || slot.editable === false}
      sx={shellSx}
    >
      <Droppable
        dndId={slot.id}
        data={{
          ...slot,
          accepts: [slotType],
          type: slotType,
          assetType: slotType,
          asset_type: slotType,
          coupon_number: slot.order,
        }}
        droppedValue={content}
        subPlaceholder={placeholder}
        sx={{
          width: "100%",
          height: "auto",
          aspectRatio: slot.aspectRatio || "1 / 1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          ...((selectedAsset || slot.editable === false) && {
            background: "transparent",
          }),
          ...sx,
        }}
        disabled={readOnly || slot.editable === false}
      />
    </SlotShell>
  );
}

export function RRDFixedImageSlot({
  fixedImages = [],
  slot,
  sx = {},
  ...props
}: {
  fixedImages?: RRDAsset[];
  slot?: RRDSlot;
  sx?: RRDSx;
  [key: string]: unknown;
}) {
  const fixedImage = fixedImages?.[0];

  return (
    <Box
      sx={{
        position: "relative",
        background: fixedImage ? "transparent" : "var(--neutral-200)",
        aspectRatio: slot?.aspectRatio || "1 / 1",
        width: "100%",
        minHeight: 96,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        border: "1px solid var(--neutral-200)",
        ...sx,
      }}
      {...props}
    >
      {fixedImage ? (
        <SlotImage asset={fixedImage} />
      ) : (
        <Typography variant="caption" fontWeight={600} textAlign="center">
          Fixed Image
        </Typography>
      )}
    </Box>
  );
}

export function RRDAddressArea({
  sx = {},
  ...props
}: {
  sx?: RRDSx;
  [key: string]: unknown;
}) {
  return (
    <Box
      sx={{
        backgroundColor: "var(--neutral-200)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 96,
        height: "100%",
        ...sx,
      }}
      {...props}
    >
      <Typography variant="body2" textAlign="center" fontWeight={600}>
        Address Area
      </Typography>
    </Box>
  );
}

export function RRDCardFace({
  children,
  aspectRatio,
  sx = {},
  ...props
}: {
  children?: ReactNode;
  aspectRatio?: string;
  sx?: RRDSx;
  [key: string]: unknown;
}) {
  return (
    <Box
      style={{ aspectRatio }}
      sx={{
        width: "100%",
        overflow: "hidden",
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

export function RRDPageLabel({ children }: { children?: ReactNode }) {
  return (
    <Typography
      variant="body2"
      color="text.primary"
      gutterBottom
      textAlign="center"
      fontWeight={700}
    >
      {children}
    </Typography>
  );
}

export function RRDExpirationText({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <Typography
      variant="caption"
      component="p"
      color="textSecondary"
      gutterBottom
      textAlign="center"
      fontWeight={600}
      sx={{ fontStyle: "italic" }}
      mt={1}
    >
      Expiration Date: {children}
    </Typography>
  );
}

export function RRDCouponGrid({
  slots,
  layoutSpec,
  heroImages,
  coupons,
  mode,
  onOpenMobileSlot,
  onRemoveAsset,
  readOnly,
  fillHeight = false,
  sx = {},
  cellSx = {},
  slotSx = {},
}: {
  slots: RRDSlot[];
  layoutSpec?: RRDLayoutSpec;
  heroImages?: RRDAsset[];
  coupons?: RRDAsset[];
  mode?: string;
  onOpenMobileSlot?: (_slot: RRDMobileSlotPayload) => void;
  onRemoveAsset?: (_slot: RRDSlot) => void;
  readOnly?: boolean;
  fillHeight?: boolean;
  sx?: RRDSx;
  cellSx?: RRDSx;
  slotSx?: RRDSx;
}) {
  const columns = layoutSpec?.couponGrid?.columns || 3;
  const rows = Math.ceil(slots.length / columns);
  const couponBorder = (theme) => `2px dashed ${theme.palette.primary.main}`;
  const fillHeightCellSx = fillHeight ? { height: `${100 / rows}%` } : {};
  const fillHeightSlotSx = fillHeight
    ? { aspectRatio: "auto", height: "100%" }
    : {};

  return (
    <Grid
      container
      spacing={0}
      sx={{
        ...(fillHeight ? { height: "100%" } : {}),
        ...sx,
      }}
    >
      {slots.map((slot, index) => {
        const row = Math.floor(index / columns);
        const column = index % columns;
        const isLastColumn =
          column === columns - 1 || index === slots.length - 1;
        const isLastRow = row === rows - 1;
        const isEditable = slot.editable !== false;

        return (
          <Grid
            key={slot.id}
            size={12 / columns}
            sx={{
              ...fillHeightCellSx,
              ...cellSx,
            }}
          >
            <RRDImageSlot
              slot={slot}
              heroImages={heroImages}
              coupons={coupons}
              mode={mode}
              onOpenMobileSlot={onOpenMobileSlot}
              onRemoveAsset={onRemoveAsset}
              readOnly={readOnly || slot.editable === false}
              placeholder="Coupon"
              sx={{
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  borderTop: isEditable ? couponBorder : "none",
                  borderLeft: isEditable ? couponBorder : "none",
                  borderRight:
                    isEditable && isLastColumn ? couponBorder : "none",
                  borderBottom: isEditable && isLastRow ? couponBorder : "none",
                },
                ...fillHeightSlotSx,
                ...slotSx,
              }}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}
